import { useState, useEffect } from 'react'
import { Dialog, Flex, Text, Button, TextField, Box, Separator, Spinner, RadioGroup } from '@radix-ui/themes'
import axios from 'axios'

import { Salenote } from '@/interfaces/Salenote'
import { TransferParty, TransferInitDto, CompraPara, TransferType } from '@/interfaces/Transfer'
import useTransferInit from '@/hooks/useTransferInit'
import { useAuth } from '@/hooks/useAuth'
import { generateContractPdf } from '@/utils/pdfGenerator'
import { makeUrlTransferDocuments } from '@/hooks/connectionData'
import { getToken } from '@/utils/utils'
import useOrganizationFetcher from '@/hooks/useOrganizationFetcher'
import { Organization } from '@/interfaces/Organization'
import { useConsignmentFetcher } from '@/hooks/useConsignmentFetcher'
import useSalenoteFetcher from '@/hooks/useSalenoteFetcher'
import Consignment from '@/interfaces/Consignment'
import { validateAndIdentifyRut, RutType } from '@/utils/rutValidation'

interface TransferInitDialogProps {
  open: boolean
  onClose: () => void
  salenote: Salenote
  onSuccess: (transferId: string) => void
}

const formatCLP = (amount: number): string => {
  return `$${amount.toLocaleString('es-CL')}`
}

export const TransferInitDialog = ({ open, onClose, salenote, onSuccess }: TransferInitDialogProps) => {
  const { userData } = useAuth()
  const { initTransfer, loading: initLoading, errorTransferInit } = useTransferInit()
  const { fetchOrganization } = useOrganizationFetcher()
  const { fetchConsignmentVehicle } = useConsignmentFetcher()
  const { fetchSalenoteTradeIn } = useSalenoteFetcher()

  const [fiscalAppraisal, setFiscalAppraisal] = useState<string>('')
  const [transferType, setTransferType] = useState<TransferType>(TransferType.STANDARD)
  const [step, setStep] = useState(1) // 1: Review, 2: Fiscal, 3: Confirm
  const [generatingPdf, setGeneratingPdf] = useState(false)
  const [resolvedSeller, setResolvedSeller] = useState<TransferParty | null>(null)
  const [loadingSeller, setLoadingSeller] = useState(false)

  const [resolvedConsignment, setResolvedConsignment] = useState<Consignment | undefined>(undefined)
  const [resolvedTradeIn, setResolvedTradeIn] = useState<Salenote | undefined>(undefined)

  // Combined loading state
  const loading = initLoading || generatingPdf || loadingSeller

  useEffect(() => {
    // Check for missing organization data and resolve seller on mount
    const resolveData = async () => {
      if (userData?.organization?._id) {
        const org = await fetchOrganization(userData.organization._id)

        if (org) {
          // Check if articles exist and have required fields
          if (!org.articles || !org.articles.notary || !org.articles.date) {
            // console.warn('Missing legal data');
          }
        }
      }

      setLoadingSeller(true)
      setResolvedConsignment(undefined)
      setResolvedTradeIn(undefined)

      // Default organization seller
      let defaultSeller: TransferParty = {
        type: 'Legal' as const,
        name: salenote.body.organization.legalName || salenote.body.organization.name,
        rut: salenote.body.organization.nin,
        address: salenote.body.organization.address,
        legalEntity: salenote.body.organization.delegates?.[0]
          ? {
              companyName: salenote.body.organization.legalName || salenote.body.organization.name,
              companyRut: salenote.body.organization.nin,
              repName: `${salenote.body.organization.delegates[0].firstname} ${salenote.body.organization.delegates[0].lastname}`,
              repRut: salenote.body.organization.delegates[0].nin,
              address: salenote.body.organization.address,
              powerDate: salenote.body.organization.articles?.date
                ? new Date(salenote.body.organization.articles.date).toLocaleDateString('es-CL')
                : '',
              documentType: 'Escritura Pública',
              notaryOffice: salenote.body.organization.articles?.municipality || '',
              notaryName: salenote.body.organization.articles?.notary || '',
            }
          : {
              companyName: salenote.body.organization.legalName || salenote.body.organization.name,
              companyRut: salenote.body.organization.nin,
              repName: 'Representante Legal',
              repRut: '',
              address: salenote.body.organization.address,
              powerDate: salenote.body.organization.articles?.date
                ? new Date(salenote.body.organization.articles.date).toLocaleDateString('es-CL')
                : '',
              documentType: 'Escritura Pública',
              notaryOffice: salenote.body.organization.articles?.municipality || '',
              notaryName: salenote.body.organization.articles?.notary || '',
            },
      }

      try {
        const vehState = salenote.body.vehicle.state

        if (vehState !== 'STATE.VEHICLE.PURCHASED') {
          // Consignment
          const consig = await fetchConsignmentVehicle(salenote.vehicle)

          if (consig && consig.body && consig.body.client) {
            setResolvedConsignment(consig)
            const rutValidation = validateAndIdentifyRut(consig.body.client.nin || '')
            const isLegal = consig.body.client.type === 'Legal Entity' || rutValidation.type === RutType.COMPANY
            const consigClientType = isLegal ? 'Legal' : 'Natural'
            const client = consig.body.client as { businessName?: string; firstname?: string; lastname?: string }

            defaultSeller = {
              type: consigClientType,
              name:
                consigClientType === 'Legal'
                  ? client.businessName || `${consig.body.client.firstname} ${consig.body.client.lastname}`
                  : `${consig.body.client.firstname} ${consig.body.client.lastname}`,
              rut: consig.body.client.nin,
              address: consig.body.client.address || '',
              legalEntity:
                consigClientType === 'Legal'
                  ? {
                      companyName:
                        client.businessName || `${consig.body.client.firstname} ${consig.body.client.lastname}`,
                      companyRut: consig.body.client.nin,
                      repName:
                        `${consig.body.client.firstname} ${consig.body.client.lastname}`.trim() ||
                        'Representante Legal',
                      repRut: '',
                      address: consig.body.client.address || '',
                      powerDate: '',
                      documentType: 'Escritura Pública',
                      notaryOffice: '',
                      notaryName: '',
                    }
                  : undefined,
            }
          }
        } else {
          // Check if it's a trade-in from another sale note
          const tradeInSalenote = await fetchSalenoteTradeIn(salenote.body.vehicle.plate)

          if (tradeInSalenote && tradeInSalenote.body && tradeInSalenote.body.client) {
            setResolvedTradeIn(tradeInSalenote)
            const rutValidation = validateAndIdentifyRut(tradeInSalenote.body.client.nin || '')
            const isLegal =
              tradeInSalenote.body.client.type === 'Legal Entity' || rutValidation.type === RutType.COMPANY
            const tradeInClientType = isLegal ? 'Legal' : 'Natural'
            const client = tradeInSalenote.body.client as {
              businessName?: string
              firstname?: string
              lastname?: string
            }

            defaultSeller = {
              type: tradeInClientType,
              name:
                tradeInClientType === 'Legal'
                  ? client.businessName ||
                    `${tradeInSalenote.body.client.firstname} ${tradeInSalenote.body.client.lastname}`
                  : `${tradeInSalenote.body.client.firstname} ${tradeInSalenote.body.client.lastname}`,
              rut: tradeInSalenote.body.client.nin,
              address: tradeInSalenote.body.client.address || '',
              legalEntity:
                tradeInClientType === 'Legal'
                  ? {
                      companyName:
                        client.businessName ||
                        `${tradeInSalenote.body.client.firstname} ${tradeInSalenote.body.client.lastname}`,
                      companyRut: tradeInSalenote.body.client.nin,
                      repName:
                        `${tradeInSalenote.body.client.firstname} ${tradeInSalenote.body.client.lastname}`.trim() ||
                        'Representante Legal',
                      repRut: '',
                      address: tradeInSalenote.body.client.address || '',
                      powerDate: '',
                      documentType: 'Escritura Pública',
                      notaryOffice: '',
                      notaryName: '',
                    }
                  : undefined,
            }
          }
        }
      } catch (err) {
        console.error('Failed to resolve seller:', err)
      }

      setResolvedSeller(defaultSeller)
      setLoadingSeller(false)
    }

    if (open) {
      resolveData()
    }
  }, [open, userData?.organization?._id, salenote])

  // Calculate fees
  const price = salenote.price
  const fiscal = Number(fiscalAppraisal) || 0
  const municipalTax = Math.round(Math.max(price, fiscal) * 0.015)
  const civilRegistryFee = 39240
  const transfieroFee = transferType === TransferType.EXPRESS ? 64900 : 24900
  const veeklsCommission = 5000
  const combinedServiceFee = transfieroFee + veeklsCommission

  const baseCost = municipalTax + civilRegistryFee + transfieroFee + veeklsCommission
  const totalCost = Math.round(baseCost / 0.9681)
  const mercadoPagoFee = totalCost - baseCost

  const MAX_INT32 = 2147483647
  const isFiscalInvalid = fiscal > MAX_INT32

  // Build buyer from salenote client
  const buyer: TransferParty = {
    type: 'Natural' as const,
    name: `${salenote.body.client.firstname} ${salenote.body.client.lastname}`,
    rut: salenote.body.client.nin,
    address: salenote.body.client.address,
  }

  // Use compraPara if enabled
  const compraPara: CompraPara | undefined = salenote.compraPara?.enabled
    ? {
        enabled: true,
        ownerName: salenote.compraPara.ownerName,
        ownerRut: salenote.compraPara.ownerRut,
      }
    : undefined

  useEffect(() => {
    if (open) {
      setStep(1)
      setFiscalAppraisal('')
      setGeneratingPdf(false)
    }
  }, [open])

  const handleSubmit = async () => {
    if (!userData?.organization?._id || !resolvedSeller) return

    setGeneratingPdf(true) // Show loading state immediately

    try {
      // 1. Fetch fresh organization data to get latest delegates
      let freshUserData = userData
      const freshOrg = await fetchOrganization(userData.organization._id)

      if (freshOrg) {
        freshUserData = {
          ...userData,
          organization: {
            ...userData.organization,
            ...freshOrg,
          } as Organization,
        }
      }

      // 2. Re-construct seller with fresh data if it's the organization
      let finalSeller = resolvedSeller

      // We only overwrite the seller with the Organization's data if it's actually the organization selling
      // We consider it the organization if it's a Propio vehicle, OR if the resolved client happens to be the organization itself (matching RUTs)
      const cleanRut = (r: string) => r.replace(/[^0-9kK]/g, '').toUpperCase()
      const isVehiclePropio =
        (!resolvedConsignment && !resolvedTradeIn) ||
        cleanRut(resolvedSeller.rut) === cleanRut(freshUserData.organization!.nin)

      if (isVehiclePropio) {
        const org = freshUserData.organization!
        const delegates = org.delegates || []

        finalSeller = {
          type: 'Legal',
          name: org.legalName || org.name,
          rut: org.nin,
          address: org.address,
          legalEntity: delegates[0]
            ? {
                companyName: org.legalName || org.name,
                companyRut: org.nin,
                repName: `${delegates[0].firstname} ${delegates[0].lastname}`,
                repRut: delegates[0].nin,
                address: org.address,
                powerDate: org.articles?.date ? new Date(org.articles.date).toLocaleDateString('es-CL') : '',
                documentType: 'Escritura Pública',
                notaryOffice: org.articles?.municipality || '',
                notaryName: org.articles?.notary || '',
              }
            : {
                companyName: org.legalName || org.name,
                companyRut: org.nin,
                repName: 'Representante Legal',
                repRut: '',
                address: org.address,
                powerDate: org.articles?.date ? new Date(org.articles.date).toLocaleDateString('es-CL') : '',
                documentType: 'Escritura Pública',
                notaryOffice: org.articles?.municipality || '',
                notaryName: org.articles?.notary || '',
              },
        }
      }

      const dto: TransferInitDto = {
        salenoteId: salenote._id,
        fiscalAppraisal: fiscal,
        buyer,
        seller: finalSeller,
        compraPara,
        mercadoPagoFee,
        organizationId: userData.organization._id,
        type: transferType,
      }

      // 3. Initialize Transfer
      const result = await initTransfer(dto)

      if (result) {
        // 4. Generate PDF using fresh data
        const pdfFile = await generateContractPdf(salenote, freshUserData, resolvedTradeIn, resolvedConsignment, fiscal)

        // 5. Upload PDF
        const token = getToken()
        const formData = new FormData()
        formData.append('images', pdfFile)
        formData.append('transferId', result.transfer._id)
        formData.append('documentType', 'draftContract')
        formData.append('organizationId', userData.organization._id)

        const apiUrl = makeUrlTransferDocuments()

        await axios.post(apiUrl, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        })

        console.log('Contract PDF generated and uploaded successfully')
        onSuccess(result.transfer._id)
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : String(err)
      console.error('Failed to init transfer or generate/upload contract PDF:', err)
      alert(`Error: ${errorMsg}`)
    } finally {
      setGeneratingPdf(false)
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && !loading && onClose()}>
      <Dialog.Content maxWidth="600px">
        <Dialog.Title>Iniciar Transferencia Vehicular</Dialog.Title>

        {step === 1 && (
          <Flex direction="column" gap="3" mt="3">
            <Text size="2" weight="bold">
              Datos del vehículo
            </Text>
            <Box className="bg-gray-50 rounded-md p-3">
              <Text size="2">
                Patente: <strong>{salenote.body.vehicle.plate}</strong>
              </Text>
              <Text size="2">
                {salenote.body.vehicle.brand} {salenote.body.vehicle.model} {salenote.body.vehicle.year}
              </Text>
            </Box>

            <Separator />

            <Text size="2" weight="bold">
              Comprador
            </Text>
            <Box className="bg-gray-50 rounded-md p-3">
              <Text size="2" className="mb-1">
                {buyer.name}
              </Text>
              {buyer.rut && (
                <Text size="2" color="gray">
                  RUT: {buyer.rut}
                </Text>
              )}
              {compraPara && (
                <Box className="mt-3 bg-amber-50 border border-amber-200 rounded p-2">
                  <Text size="1" weight="bold" className="block mb-1">
                    Compra para tercero:
                  </Text>
                  <Text size="2">
                    {compraPara.ownerName}
                    {compraPara.ownerRut ? ` - ${compraPara.ownerRut}` : ''}
                  </Text>
                </Box>
              )}
            </Box>

            <Separator />

            <Text size="2" weight="bold">
              Vendedor
            </Text>
            <Box className="bg-gray-50 rounded-md p-3">
              {loadingSeller ? (
                <Flex align="center" gap="2">
                  <Spinner size="1" />
                  <Text size="2" color="gray">
                    Resolviendo vendedor...
                  </Text>
                </Flex>
              ) : resolvedSeller ? (
                <>
                  <Text size="2" className="mb-1">
                    {resolvedSeller.name}
                  </Text>
                  {resolvedSeller.rut && (
                    <Text size="2" color="gray">
                      RUT: {resolvedSeller.rut}
                    </Text>
                  )}
                </>
              ) : (
                <Text size="2" color="red">
                  No se pudo resolver el vendedor
                </Text>
              )}
            </Box>

            <Flex gap="3" mt="4" justify="end">
              <Button variant="soft" color="gray" onClick={onClose}>
                Cancelar
              </Button>
              <Button onClick={() => setStep(2)} disabled={loadingSeller || !resolvedSeller}>
                Continuar
              </Button>
            </Flex>
          </Flex>
        )}

        {step === 2 && (
          <Flex direction="column" gap="3" mt="3">
            <Box>
              <Text size="2" weight="bold" mb="2" as="div">
                Tipo de transferencia
              </Text>
              <RadioGroup.Root
                defaultValue={TransferType.STANDARD}
                onValueChange={(val) => setTransferType(val as TransferType)}
              >
                <Flex gap="4">
                  <RadioGroup.Item value={TransferType.STANDARD}>
                    <Flex direction="column">
                      <Text size="2" weight="bold">
                        ESTÁNDAR
                      </Text>
                      <Text size="1" color="gray">
                        PROCESO NORMAL ($29.900)
                      </Text>
                    </Flex>
                  </RadioGroup.Item>
                  <RadioGroup.Item value={TransferType.EXPRESS}>
                    <Flex direction="column">
                      <Text size="2" weight="bold" color="teal">
                        EXPRESS
                      </Text>
                      <Text size="1" color="gray">
                        MÁS RÁPIDO ($69.900)
                      </Text>
                    </Flex>
                  </RadioGroup.Item>
                </Flex>
              </RadioGroup.Root>
            </Box>

            <Text size="2" weight="bold">
              Tasación Fiscal
            </Text>
            <Text size="2" color="gray">
              Ingrese la tasación fiscal del vehículo (del SII). Se usa para calcular el impuesto municipal.
            </Text>

            {salenote.body.vehicle.appraisal !== undefined && (
              <Box className="bg-amber-50 border border-amber-200 rounded-md p-3 my-2">
                <Text size="2" weight="bold" mb="1" style={{ color: '#b45309' }} as="div">
                  Valor tasacion en ficha vehiculo: {formatCLP(salenote.body.vehicle.appraisal)}
                </Text>
                <Text size="1" color="gray" mb="2" as="div">
                  La nota de venta original contiene este valor. Puede mantenerlo o ingresar uno nuevo actualizado.
                </Text>
                <Button
                  variant="soft"
                  color="amber"
                  size="1"
                  onClick={() => setFiscalAppraisal(salenote.body.vehicle.appraisal.toString())}
                >
                  Usar valor original
                </Button>
              </Box>
            )}

            <TextField.Root
              placeholder="Ej: 14000000"
              type="number"
              value={fiscalAppraisal}
              onChange={(e) => setFiscalAppraisal(e.target.value)}
              color={isFiscalInvalid ? 'red' : undefined}
            />

            {isFiscalInvalid && (
              <Text size="1" color="red">
                El valor excede el límite permitido (2.1 mil millones)
              </Text>
            )}

            {fiscal > 0 && (
              <Box className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <Text size="2" weight="bold">
                  Desglose de costos
                </Text>
                <Flex direction="column" gap="1" mt="2">
                  <Flex justify="between">
                    <Text size="2">Impuesto municipal (1.5%)</Text>
                    <Text size="2">{formatCLP(municipalTax)}</Text>
                  </Flex>
                  <Flex justify="between">
                    <Text size="2">Arancel Registro Civil</Text>
                    <Text size="2">{formatCLP(civilRegistryFee)}</Text>
                  </Flex>
                  <Flex justify="between">
                    <Text size="2">Servicio transferencia</Text>
                    <Text size="2">{formatCLP(combinedServiceFee)}</Text>
                  </Flex>
                  <Flex justify="between">
                    <Text size="2">Comisión Mercado Pago</Text>
                    <Text size="2">{formatCLP(mercadoPagoFee)}</Text>
                  </Flex>
                  <Separator />
                  <Flex justify="between">
                    <Text size="2" weight="bold">
                      Total
                    </Text>
                    <Text size="2" weight="bold" color="blue">
                      {formatCLP(totalCost)}
                    </Text>
                  </Flex>
                </Flex>
              </Box>
            )}

            <Flex gap="3" mt="4" justify="end">
              <Button variant="soft" color="gray" onClick={() => setStep(1)}>
                Volver
              </Button>
              <Button disabled={fiscal <= 0 || isFiscalInvalid} onClick={() => setStep(3)}>
                Confirmar y Crear
              </Button>
            </Flex>
          </Flex>
        )}

        {step === 3 && (
          <Flex direction="column" gap="3" mt="3">
            <Text size="2" weight="bold">
              Confirmar Transferencia
            </Text>
            <Text size="2" color="gray">
              Se creará la transferencia. Después de subir los documentos requeridos, podrá enviarla directamente a
              Transfiero Online.
            </Text>

            {errorTransferInit && (
              <Box className="bg-red-50 border border-red-200 rounded-md p-2">
                <Text size="2" color="red">
                  {errorTransferInit.message}
                </Text>
              </Box>
            )}

            <Flex gap="3" mt="4" justify="end">
              <Button variant="soft" color="gray" onClick={() => setStep(2)} disabled={loading}>
                Volver
              </Button>
              <Button onClick={handleSubmit} disabled={loading} color="green">
                {loading ? (
                  <Flex align="center" gap="2">
                    <Spinner size="1" />
                    {generatingPdf ? 'Generando contrato...' : 'Creando...'}
                  </Flex>
                ) : (
                  'Crear Transferencia'
                )}
              </Button>
            </Flex>
          </Flex>
        )}
      </Dialog.Content>
    </Dialog.Root>
  )
}
